import { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { Dispatch } from '@reduxjs/toolkit'
import noop from 'lodash/noop'
import { createSelector } from 'reselect'

import { usePrevious } from './hooks/usePrevious'
import { humanizeMinutes } from './lib/formatters'
import {
  addNotification as addNotificationAction,
  removeNotification as removeNotificationAction,
  updateNotification as updateNotificationAction,
} from './notifications/notifications-actions'
import type { NotificationType } from './notifications/types'
import type { GlobalState } from './types'

const expirationTime = 3600000 // 1 hr
const warnTime = 2700000 // 45 min

type ExpiryProps = {
  valid: boolean
  isUploading: boolean
  notifications: {
    notifications: NotificationType[]
  }
  duration: number
  warnAt: number
  claimError(): void
  addNotification(notification: NotificationType): void
  removeNotification(notification: string): void
  updateNotification(notificationName: string, message: string): void
}

type ExpiryState = {
  started: boolean
  expireTime: number
  warnAt?: number
  timeRemaining: number
  interval: NodeJS.Timeout
  timesUp?: boolean
  startTimer: boolean
}

const initState: ExpiryState = {
  started: false,
  expireTime: Date.now() + expirationTime,
  warnAt: undefined,
  timeRemaining: Number.MAX_SAFE_INTEGER,
  interval: setInterval(noop, 1000),
  timesUp: false,
  startTimer: false
}

const EXPIRED_MESSAGE =
  'Sorry, your session expired. Please enter the information below to start over.'
const COUNTDOWN_TITLE =
  'SECURE SESSION EXPIRING SOON'
const getMessageBody = (ms: number) =>
  `As a security precaution, your session will expire in <strong>${
    humanizeMinutes(Math.round(ms / 1000))
  }</strong>. Any information that you entered will be lost.`

const isConfirmationPage = () => window.location.pathname === `${window.travURI}confirmation`

const ExpiryComponent = (props: GlobalState & ExpiryProps) => {
  const { pathname } = useLocation()
  const [state, setState] = useState<ExpiryState>(initState)
  const prevState = usePrevious<ExpiryState>(state)
  const navigate = useNavigate()
  const {
    valid,
    isUploading,
    notifications: {
      notifications
    },
    duration = expirationTime,
    warnAt = warnTime,
    addNotification,
    removeNotification,
    updateNotification,
  } = props
  const {
    started,
    timeRemaining,
    interval: stateInterval,
    timesUp,
    warnAt: stateWarnAt,
  } = state

  const shouldStart = useCallback(() => {
    // Do not start timer on claim page. Only start after claim page.
    if (!started && valid && pathname !== window.travURI) {
      setState({ ...state, started: true, warnAt: Date.now() + warnTime })
    }
  }, [started, state, valid, pathname])

  // updated from componentDidMount
  useEffect(() => {
    // only want to run once on mount, when prevState is undefined
    if (!prevState) {
      shouldStart()
    }
  }, [shouldStart, prevState])

  // updated from componentWillUnmount
  useEffect(() => {
    return () => clearTimeout(stateInterval)
  }, [stateInterval])

  const resetCountdown = useCallback(() => {
    clearInterval(stateInterval)
    setState(initState)
  }, [stateInterval])

  const startCountdown = useCallback(() => {
    clearTimeout(stateInterval)
    const interval = setInterval(() => {
      // time has expired, set timesUp in state to trigger the handleTimesUp function if applicable.
      setState(oldState => {
        if (oldState.timeRemaining <= 0) {
          return { ...oldState, timesUp: true }
        }
        return oldState
      })

      // Time to warn has happened. Set startTimer in state to trigger a metric call that the
      // warning is displayed.
      setState(oldState => {
        if (
          oldState.warnAt && oldState.warnAt < Date.now()
          && notifications.length === 0 && !isUploading
        ) {
          return { ...oldState, startTimer: true }
        }
        return oldState
      })

      // Count down time remaining each second the interval is run.
      setState(oldState => ({ ...oldState, timeRemaining: oldState.expireTime - Date.now() }))
    }, 1000)

    const now = Date.now()
    setState({
      ...state,
      expireTime: now + duration,
      warnAt: now + warnAt,
      startTimer: false,
      interval
    })
  }, [
    state,
    duration,
    isUploading,
    notifications.length,
    stateInterval,
    warnAt
  ])

  useEffect(() => {
    if (
      stateWarnAt && stateWarnAt < Date.now()
      && notifications.length === 0 && !isUploading && !isConfirmationPage()
    ) {
      addNotification({
        name: 'expiry',
        title: COUNTDOWN_TITLE,
        body: getMessageBody(timeRemaining),
        severity: 'warning'
      })
    } else {
      updateNotification('expiry', getMessageBody(timeRemaining))
    }
  }, [
    addNotification,
    updateNotification,
    stateWarnAt,
    timesUp,
    notifications.length,
    isUploading,
    timeRemaining
  ])

  const handleTimesUp = useCallback(() => {
    removeNotification('expiry')
    navigate(window.travURI, { state: { claimError: EXPIRED_MESSAGE } })
    resetCountdown()
    if (window.onbeforeunload) window.onbeforeunload = null
  }, [
    removeNotification,
    resetCountdown,
    navigate
  ])

  // updated from setState callback.
  useEffect(() => {
    if (started && !prevState?.started) {
      startCountdown()
    }
  }, [started, startCountdown, prevState?.started])

  useEffect(() => {
    if (!started) {
      shouldStart()
    }
  }, [started, shouldStart])

  useEffect(() => {
    if (started && !valid) {
      resetCountdown()
    }
  }, [started, valid, resetCountdown])

  useEffect(() => {
    if (timesUp && !isConfirmationPage() && !isUploading) {
      handleTimesUp()
    }
  }, [timesUp, handleTimesUp, isUploading])

  useEffect(() => {
    if (notifications.length > 0 && (isConfirmationPage() || isUploading)) {
      removeNotification('expiry')
    }
  }, [notifications.length, isUploading, removeNotification, resetCountdown])

  return (<div></div>)
}

const notificationSelector = (state: GlobalState) => state.notifications

const getNotifications = createSelector(
  notificationSelector,
  notifications => notifications.notifications.filter(n => n.name === 'expiry')
)


const mapStateToProps = (state: GlobalState) => ({
  valid: state.claim.valid,
  isUploading: state.upload.requesting,
  notifications: {
    notifications: getNotifications(state)
  }
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  removeNotification: (name: string) => dispatch(removeNotificationAction(name)),
  addNotification: (options: NotificationType) => dispatch(addNotificationAction(options)),
  updateNotification: (name: string, body: string) => dispatch(updateNotificationAction(name, body))
})

const Expiry = connect(mapStateToProps, mapDispatchToProps)(ExpiryComponent)

export {
  COUNTDOWN_TITLE,
  expirationTime,
  EXPIRED_MESSAGE,
  Expiry,
  ExpiryComponent,
  getMessageBody,
  warnTime
}

export type { ExpiryProps }
